import { useState } from 'react';
import { MdPerson, MdPeople, MdSearch, MdAdd, MdPersonAdd, MdRemove, MdPublish } from 'react-icons/md';
import AlertPopup from 'components/popup/AlertPopup';
import ConfirmPopup from 'components/popup/ConfirmPopup';

interface Mentor {
  id: string;
  name: string;
  skills: string[];
  maxMentees: number;
  avatar: string;
  assignedMentees: Mentee[];
}

interface Mentee {
  id: string;
  name: string;
  interests: string[];
  avatar: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: 'mentor' | 'mentee';
  skills: string[];
  interests: string[];
  avatar: string;
  isInAssignment?: boolean;
}

const MentorAssignments = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [draggedMentee, setDraggedMentee] = useState<Mentee | null>(null);
  const [draggedUser, setDraggedUser] = useState<User | null>(null);
  const [showUserSearch, setShowUserSearch] = useState(false);

  // Popup states
  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean;
    message: string;
    title: string;
    variant: 'success' | 'warning' | 'info' | 'error';
  }>({
    isOpen: false,
    message: '',
    title: 'Alert',
    variant: 'info',
  });
  const [confirmConfig, setConfirmConfig] = useState<{
    isOpen: boolean;
    message: string;
    title: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    message: '',
    title: 'Confirm',
    onConfirm: () => {},
  });

  // Mock data with assigned mentees
  const [mentors, setMentors] = useState<Mentor[]>([
    {
      id: '1',
      name: 'Dr. Sarah Chen',
      skills: ['Machine Learning', 'Data Science', 'Python'],
      maxMentees: 10,
      avatar: '/api/placeholder/40/40',
      assignedMentees: [
        {
          id: '4',
          name: 'John Smith',
          interests: ['Machine Learning', 'AI'],
          avatar: '/api/placeholder/32/32'
        },
        {
          id: '5',
          name: 'Lisa Wang',
          interests: ['Data Science', 'Python'],
          avatar: '/api/placeholder/32/32'
        }
      ]
    },
    {
      id: '2',
      name: 'Jennifer Kim',
      skills: ['UX Design', 'Product Management', 'Figma'],
      maxMentees: 10,
      avatar: '/api/placeholder/40/40',
      assignedMentees: [
        {
          id: '6',
          name: 'Sophie Chen',
          interests: ['UX Design', 'User Research'],
          avatar: '/api/placeholder/32/32'
        }
      ]
    },
    {
      id: '3',
      name: 'Michael Brown',
      skills: ['Software Engineering', 'React', 'Node.js'],
      maxMentees: 10,
      avatar: '/api/placeholder/40/40',
      assignedMentees: []
    }
  ]);

  const [unassignedMentees, setUnassignedMentees] = useState<Mentee[]>([
    {
      id: '1',
      name: 'Alex Rodriguez',
      interests: ['Data Science', 'Machine Learning'],
      avatar: '/api/placeholder/32/32'
    },
    {
      id: '2',
      name: 'Emma Thompson',
      interests: ['UX Design', 'User Research'],
      avatar: '/api/placeholder/32/32'
    },
    {
      id: '3',
      name: 'David Wilson',
      interests: ['Web Development', 'JavaScript'],
      avatar: '/api/placeholder/32/32'
    }
  ]);

  // All users in the system (not yet in assignment)
  const [allUsers] = useState<User[]>([
    {
      id: '7',
      name: 'Prof. Maria Garcia',
      email: 'maria.garcia@chemistry.edu',
      role: 'mentor',
      skills: ['Organic Chemistry', 'Research Methods', 'Laboratory Management'],
      interests: [],
      avatar: '/api/placeholder/40/40',
      isInAssignment: false
    },
    {
      id: '8',
      name: 'James Wilson',
      email: 'james.wilson@marketing.com',
      role: 'mentee',
      skills: [],
      interests: ['Digital Marketing', 'Brand Strategy', 'Social Media'],
      avatar: '/api/placeholder/32/32',
      isInAssignment: false
    },
    {
      id: '9',
      name: 'Dr. Ahmed Khan',
      email: 'ahmed.khan@physics.edu',
      role: 'mentor',
      skills: ['Theoretical Physics', 'Quantum Mechanics', 'Research'],
      interests: [],
      avatar: '/api/placeholder/40/40',
      isInAssignment: false
    },
    {
      id: '10',
      name: 'Rachel Green',
      email: 'rachel.green@startup.io',
      role: 'mentee',
      skills: [],
      interests: ['Product Management', 'Startup', 'Business Strategy'],
      avatar: '/api/placeholder/32/32',
      isInAssignment: false
    },
    {
      id: '11',
      name: 'Kevin Liu',
      email: 'kevin.liu@architecture.com',
      role: 'mentor',
      skills: ['Sustainable Architecture', 'Urban Planning', 'Building Design'],
      interests: [],
      avatar: '/api/placeholder/40/40',
      isInAssignment: false
    },
    {
      id: '12',
      name: 'Sarah Johnson',
      email: 'sarah.johnson@student.edu',
      role: 'mentee',
      skills: [],
      interests: ['International Relations', 'Diplomacy', 'Policy Analysis'],
      avatar: '/api/placeholder/32/32',
      isInAssignment: false
    },
    {
      id: '13',
      name: 'Dr. Emily Chen',
      email: 'emily.chen@medschool.edu',
      role: 'mentor',
      skills: ['Biomedical Engineering', 'Medical Device Design', 'Healthcare Innovation'],
      interests: [],
      avatar: '/api/placeholder/40/40',
      isInAssignment: false
    },
    {
      id: '14',
      name: 'Marcus Thompson',
      email: 'marcus.thompson@student.com',
      role: 'mentee',
      skills: [],
      interests: ['Environmental Science', 'Renewable Energy', 'Sustainability'],
      avatar: '/api/placeholder/32/32',
      isInAssignment: false
    },
    {
      id: '15',
      name: 'Prof. Isabella Rodriguez',
      email: 'isabella.rodriguez@finance.edu',
      role: 'mentor',
      skills: ['Financial Modeling', 'Investment Banking', 'Risk Management'],
      interests: [],
      avatar: '/api/placeholder/40/40',
      isInAssignment: false
    },
    {
      id: '16',
      name: 'Tyler Adams',
      email: 'tyler.adams@artschool.edu',
      role: 'mentee',
      skills: [],
      interests: ['Digital Media', 'Animation', '3D Modeling'],
      avatar: '/api/placeholder/32/32',
      isInAssignment: false
    },
    {
      id: '17',
      name: 'Dr. Robert Kim',
      email: 'robert.kim@aerospace.com',
      role: 'mentor',
      skills: ['Aerospace Engineering', 'Satellite Systems', 'Space Technology'],
      interests: [],
      avatar: '/api/placeholder/40/40',
      isInAssignment: false
    },
    {
      id: '18',
      name: 'Sophia Martinez',
      email: 'sophia.martinez@psych.edu',
      role: 'mentee',
      skills: [],
      interests: ['Psychology', 'Mental Health', 'Behavioral Research'],
      avatar: '/api/placeholder/32/32',
      isInAssignment: false
    },
    {
      id: '19',
      name: 'Prof. David Park',
      email: 'david.park@journalism.edu',
      role: 'mentor',
      skills: ['Digital Journalism', 'Media Ethics', 'Content Strategy'],
      interests: [],
      avatar: '/api/placeholder/40/40',
      isInAssignment: false
    },
    {
      id: '20',
      name: 'Nina Patel',
      email: 'nina.patel@lawschool.edu',
      role: 'mentee',
      skills: [],
      interests: ['Intellectual Property Law', 'Legal Technology', 'Contract Law'],
      avatar: '/api/placeholder/32/32',
      isInAssignment: false
    },
    {
      id: '21',
      name: 'Dr. Michael Foster',
      email: 'michael.foster@culinary.edu',
      role: 'mentor',
      skills: ['Culinary Arts', 'Restaurant Management', 'Food Science'],
      interests: [],
      avatar: '/api/placeholder/40/40',
      isInAssignment: false
    },
    {
      id: '22',
      name: 'Alex Wang',
      email: 'alex.wang@music.edu',
      role: 'mentee',
      skills: [],
      interests: ['Music Production', 'Audio Engineering', 'Sound Design'],
      avatar: '/api/placeholder/32/32',
      isInAssignment: false
    }
  ]);

  const handleDragStart = (e: React.DragEvent, mentee: Mentee) => {
    setDraggedMentee(mentee);
    setDraggedUser(null);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleUserDragStart = (e: React.DragEvent, user: User) => {
    setDraggedUser(user);
    setDraggedMentee(null);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, mentorId: string) => {
    e.preventDefault();
    
    const mentor = mentors.find(m => m.id === mentorId);
    if (!mentor || mentor.assignedMentees.length >= mentor.maxMentees) return;

    if (draggedMentee) {
      // Add existing mentee to mentor
      setMentors(prev => prev.map(m => 
        m.id === mentorId 
          ? { ...m, assignedMentees: [...m.assignedMentees, draggedMentee] }
          : m
      ));

      // Remove from unassigned
      setUnassignedMentees(prev => prev.filter(m => m.id !== draggedMentee.id));
      setDraggedMentee(null);
    } else if (draggedUser && draggedUser.role === 'mentee') {
      // Add user as mentee directly to mentor
      const newMentee: Mentee = {
        id: draggedUser.id,
        name: draggedUser.name,
        interests: draggedUser.interests,
        avatar: draggedUser.avatar
      };

      setMentors(prev => prev.map(m => 
        m.id === mentorId 
          ? { ...m, assignedMentees: [...m.assignedMentees, newMentee] }
          : m
      ));
      setDraggedUser(null);
    }
  };

  const handleDropToUnassigned = (e: React.DragEvent) => {
    e.preventDefault();

    if (draggedUser && draggedUser.role === 'mentee') {
      // Add user to unassigned mentees
      const newMentee: Mentee = {
        id: draggedUser.id,
        name: draggedUser.name,
        interests: draggedUser.interests,
        avatar: draggedUser.avatar
      };
      setUnassignedMentees(prev => [...prev, newMentee]);
      setDraggedUser(null);
    } else if (draggedUser && draggedUser.role === 'mentor') {
      // Add user as mentor
      const newMentor: Mentor = {
        id: draggedUser.id,
        name: draggedUser.name,
        skills: draggedUser.skills,
        maxMentees: 10,
        avatar: draggedUser.avatar,
        assignedMentees: []
      };
      setMentors(prev => [...prev, newMentor]);
      setDraggedUser(null);
    }
  };

  const handleRemoveMentee = (mentorId: string, menteeId: string) => {
    const mentor = mentors.find(m => m.id === mentorId);
    const menteeToRemove = mentor?.assignedMentees.find(m => m.id === menteeId);
    
    if (!menteeToRemove) return;

    // Remove from mentor
    setMentors(prev => prev.map(m => 
      m.id === mentorId 
        ? { ...m, assignedMentees: m.assignedMentees.filter(mentee => mentee.id !== menteeId) }
        : m
    ));

    // Add back to unassigned
    setUnassignedMentees(prev => [...prev, menteeToRemove]);
  };

  const handleRemoveMentor = (mentorId: string) => {
    const mentor = mentors.find(m => m.id === mentorId);
    if (!mentor) return;

    // Add all assigned mentees back to unassigned
    if (mentor.assignedMentees.length > 0) {
      setUnassignedMentees(prev => [...prev, ...mentor.assignedMentees]);
    }

    // Remove the mentor
    setMentors(prev => prev.filter(m => m.id !== mentorId));
  };

  const handleRemoveUnassignedMentee = (menteeId: string) => {
    // Remove mentee completely from assignment pool
    setUnassignedMentees(prev => prev.filter(m => m.id !== menteeId));
  };


  const handleAddUserToAssignment = (user: User) => {
    if (user.role === 'mentor') {
      // Add as mentor
      const newMentor: Mentor = {
        id: user.id,
        name: user.name,
        skills: user.skills,
        maxMentees: 10, // Default capacity
        avatar: user.avatar,
        assignedMentees: []
      };
      setMentors(prev => [...prev, newMentor]);
    } else {
      // Add as mentee
      const newMentee: Mentee = {
        id: user.id,
        name: user.name,
        interests: user.interests,
        avatar: user.avatar
      };
      setUnassignedMentees(prev => [...prev, newMentee]);
    }
  };

  const filteredMentees = unassignedMentees.filter(mentee => 
    mentee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mentee.interests.some(interest => 
      interest.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Filter users not already in assignment
  const availableUsers = allUsers.filter(user => {
    // Check if user is already a mentor
    const isAlreadyMentor = mentors.some(m => m.id === user.id);
    // Check if user is already a mentee
    const isAlreadyMentee = unassignedMentees.some(m => m.id === user.id) || 
                           mentors.some(mentor => mentor.assignedMentees.some(m => m.id === user.id));
    
    return !isAlreadyMentor && !isAlreadyMentee;
  });

  const filteredUsers = availableUsers.filter(user =>
    user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.skills.some(skill => skill.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
    user.interests.some(interest => interest.toLowerCase().includes(userSearchTerm.toLowerCase()))
  );

  // Helper function to check if skills/interests match
  const getMatchingAttributes = (userAttributes: string[], mentorSkills: string[]) => {
    return userAttributes.map(attr => {
      const isMatching = mentorSkills.some(skill => 
        skill.toLowerCase().includes(attr.toLowerCase()) || 
        attr.toLowerCase().includes(skill.toLowerCase())
      );
      return { attribute: attr, isMatching };
    });
  };

  const handlePublishAssignment = () => {
    const totalMentors = mentors.length;
    const totalAssignedMentees = mentors.reduce((sum, mentor) => sum + mentor.assignedMentees.length, 0);
    const unassignedCount = unassignedMentees.length;

    if (totalMentors === 0) {
      setAlertConfig({
        isOpen: true,
        message: 'Please add at least one mentor before publishing.',
        title: 'Cannot Publish',
        variant: 'warning',
      });
      return;
    }

    if (totalAssignedMentees === 0 && unassignedCount === 0) {
      setAlertConfig({
        isOpen: true,
        message: 'Please add at least one mentee before publishing.',
        title: 'Cannot Publish',
        variant: 'warning',
      });
      return;
    }

    const confirmMessage = `Publish assignment with:\n• ${totalMentors} mentor(s)\n• ${totalAssignedMentees} assigned mentee(s)\n• ${unassignedCount} unassigned mentee(s)\n\nThis will notify all participants. Continue?`;

    setConfirmConfig({
      isOpen: true,
      message: confirmMessage,
      title: 'Publish Assignment',
      onConfirm: () => {
        // Here you would typically make an API call to publish the assignment
        console.log('Publishing assignment:', { mentors, unassignedMentees });
        setAlertConfig({
          isOpen: true,
          message: 'Assignment published successfully! All participants have been notified.',
          title: 'Success',
          variant: 'success',
        });
      },
    });
  };

  return (
    <div className="mt-3 h-full w-full">
      {/* User Search Section - Always visible header */}
      <div className="mb-6 bg-white dark:bg-navy-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h5 className="text-lg font-semibold text-navy-700 dark:text-white mb-2">
              Add Users to Assignment ({availableUsers.length} available)
            </h5>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Search and add mentors/mentees to the assignment pool
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowUserSearch(!showUserSearch)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                showUserSearch
                  ? 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  : 'bg-brand-500 text-white hover:bg-brand-600'
              }`}
            >
              <MdPersonAdd className="h-4 w-4" />
              {showUserSearch ? 'Hide Users' : 'Show Users'}
            </button>
            <button
              onClick={handlePublishAssignment}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-500 text-white hover:bg-green-600 transition-colors"
            >
              <MdPublish className="h-4 w-4" />
              Publish Assignment
            </button>
          </div>
        </div>

        {/* Collapsible Content */}
        {showUserSearch && (
          <div>
            <div className="relative mb-4">
              <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name, email, or skills..."
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-navy-800 dark:text-white"
              />
            </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-64 overflow-y-auto">
            {filteredUsers.length === 0 ? (
              <div className="col-span-full text-center py-8 text-gray-500">
                <MdPeople className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>{userSearchTerm ? 'No users found matching search' : 'All available users are already in assignment'}</p>
              </div>
            ) : (
              filteredUsers.map((user) => (
                <div
                  key={user.id}
                  draggable
                  onDragStart={(e) => handleUserDragStart(e, user)}
                  className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg dark:border-gray-600 hover:shadow-md transition-all cursor-move"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <MdPerson className="h-5 w-5 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h6 className="font-medium text-navy-700 dark:text-white text-sm">
                        {user.name}
                      </h6>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'mentor'
                          ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/20 dark:text-brand-300'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                      }`}>
                        {user.role}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {user.email}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {user.role === 'mentor' ? (
                        user.skills.slice(0, 2).map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                          >
                            {skill}
                          </span>
                        ))
                      ) : (
                        user.interests.slice(0, 2).map((interest, index) => (
                          <span
                            key={index}
                            className="inline-flex px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                          >
                            {interest}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleAddUserToAssignment(user)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-500 text-white hover:bg-brand-600 transition-colors"
                  >
                    <MdAdd className="h-3 w-3" />
                    Add
                  </button>
                </div>
              ))
            )}
          </div>
          </div>
        )}
      </div>

      {/* Assignment Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Mentors Section */}
      <div className={`bg-white dark:bg-navy-800 rounded-lg border-2 p-6 transition-all ${
        draggedUser && draggedUser.role === 'mentor'
          ? 'border-brand-300 bg-brand-50 dark:bg-brand-900/10 border-dashed'
          : 'border-gray-200 dark:border-gray-600'
      }`}
      onDragOver={draggedUser && draggedUser.role === 'mentor' ? handleDragOver : undefined}
      onDrop={draggedUser && draggedUser.role === 'mentor' ? handleDropToUnassigned : undefined}
      >
        <div className="mb-6">
          <h5 className="text-lg font-semibold text-navy-700 dark:text-white mb-2">
            Mentors ({mentors.length})
          </h5>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Drag mentees from the right panel to assign them to mentors
          </p>
        </div>

        <div className="space-y-4">
          {mentors.map((mentor) => (
            <div
              key={mentor.id}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, mentor.id)}
              className={`p-4 rounded-lg border-2 border-dashed transition-all group ${
                (draggedMentee || (draggedUser && draggedUser.role === 'mentee')) && mentor.assignedMentees.length < mentor.maxMentees
                  ? 'border-brand-300 bg-brand-50 dark:bg-brand-900/10'
                  : 'border-gray-200 dark:border-gray-600'
              }`}
            >
              {/* Mentor Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                  <MdPerson className="h-6 w-6 text-gray-600" />
                </div>
                <div className="flex-1">
                  <h6 className="font-semibold text-navy-700 dark:text-white">
                    {mentor.name}
                  </h6>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {mentor.assignedMentees.length}/{mentor.maxMentees} mentees
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {mentor.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="inline-flex px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveMentor(mentor.id)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-all rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100"
                  title="Remove mentor from assignment"
                >
                  <MdRemove className="h-5 w-5" />
                </button>
              </div>

              {/* Capacity Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div
                  className={`h-2 rounded-full transition-all ${
                    mentor.assignedMentees.length >= mentor.maxMentees
                      ? 'bg-red-500'
                      : 'bg-brand-500'
                  }`}
                  style={{
                    width: `${(mentor.assignedMentees.length / mentor.maxMentees) * 100}%`
                  }}
                ></div>
              </div>

              {/* Assigned Mentees */}
              <div className="space-y-2">
                {mentor.assignedMentees.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                    <MdPeople className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Drop mentees here</p>
                  </div>
                ) : (
                  mentor.assignedMentees.map((mentee) => (
                    <div
                      key={mentee.id}
                      className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg group"
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                        <MdPerson className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-navy-700 dark:text-white text-sm">
                          {mentee.name}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {getMatchingAttributes(mentee.interests, mentor.skills).map(({ attribute, isMatching }, index) => (
                            <span
                              key={index}
                              className={`inline-flex px-1.5 py-0.5 rounded text-xs ${
                                isMatching
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                              }`}
                            >
                              {attribute}
                            </span>
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveMentee(mentor.id, mentee.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-all rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Remove mentee from mentor"
                      >
                        <MdRemove className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mentees Section - Sticky */}
      <div 
        className={`bg-white dark:bg-navy-800 rounded-lg border-2 border-dashed p-6 transition-all sticky top-24 self-start ${
          draggedUser 
            ? 'border-brand-300 bg-brand-50 dark:bg-brand-900/10'
            : 'border-gray-200 dark:border-gray-600'
        }`}
        onDragOver={handleDragOver}
        onDrop={handleDropToUnassigned}
      >
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h5 className="text-lg font-semibold text-navy-700 dark:text-white mb-2">
                Unassigned Mentees ({unassignedMentees.length})
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Drag mentees to assign them to mentors
              </p>
            </div>
          </div>
          
          <div className="relative">
            <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search mentees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-navy-800 dark:text-white"
            />
          </div>
        </div>

        <div className="space-y-3">
          {filteredMentees.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MdPeople className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No unassigned mentees found</p>
            </div>
          ) : (
            filteredMentees.map((mentee) => (
              <div
                key={mentee.id}
                draggable
                onDragStart={(e) => handleDragStart(e, mentee)}
                className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg cursor-move hover:shadow-md transition-all dark:border-gray-600 bg-white dark:bg-navy-700 group"
              >
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                  <MdPerson className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <h6 className="font-medium text-navy-700 dark:text-white">
                    {mentee.name}
                  </h6>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {mentee.interests.map((interest, index) => (
                      <span
                        key={index}
                        className="inline-flex px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveUnassignedMentee(mentee.id)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-all rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100"
                  title="Remove mentee from assignment pool"
                >
                  <MdRemove className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>

      {/* Alert Popup */}
      <AlertPopup
        isOpen={alertConfig.isOpen}
        onClose={() => setAlertConfig({ ...alertConfig, isOpen: false })}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
      />

      {/* Confirm Popup */}
      <ConfirmPopup
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText="Publish"
        cancelText="Cancel"
        variant="default"
      />
    </div>
  );
};

export default MentorAssignments;